import { LegalPageLayout } from "../components/LegalPageLayout";

export function OfficialRules() {
  return (
    <LegalPageLayout
      title="Official Program Rules"
      subtitle="Know Your Dough — UST 2026"
      lastUpdated="April 3, 2026"
    >
      <p className="uppercase-notice">
        NO PURCHASE OR PAYMENT OF ANY KIND NECESSARY TO ENTER OR WIN. A PURCHASE WILL NOT INCREASE YOUR CHANCES OF WINNING.
      </p>

      <section>
        <h2>1. Sponsor</h2>
        <p>
          Know Your Dough — UST 2026 ("Program") is sponsored by BetterTime, Co., 370 Wabasha St N, Floor 8, St. Paul, MN 55102 ("Sponsor"), presented by U.S. Bank. The Program is administered by BetterTime, Co.
        </p>
      </section>

      <section>
        <h2>2. Eligibility</h2>

        <h3>2.1 General Eligibility (all participants)</h3>
        <p>
          The Program is open to individuals who, as of their date of entry: (a) are legal residents of the United States; (b) are 18 years of age or older; (c) are currently enrolled at the University of St. Thomas for the Spring 2026 semester; and (d) download the Dough app and enter the program access code during the Program Period. Void where prohibited or restricted by law.
        </p>

        <h3>2.2 Weekly Sim Competition Eligibility</h3>
        <p>
          To be eligible to receive Weekly Sim Competition prizes, participants must meet all General Eligibility requirements above.
        </p>

        <h3>2.3 Grand Prize Drawing Eligibility</h3>
        <p>
          To be eligible for the Grand Prize Drawing, participants must meet all General Eligibility requirements and additionally: (a) be enrolled as an undergraduate student; and (b) not be graduating at the end of the Spring 2026 semester. Students who will remain enrolled for at least one additional semester are eligible. For the avoidance of doubt, enrollment in any Summer 2026 coursework at the University of St. Thomas qualifies — a student does not need to be returning in the fall semester to be eligible for the Grand Prize Drawing. Graduate students and undergraduate students graduating at the end of Spring 2026 are not eligible for the Grand Prize Drawing but may participate in Weekly Sim Competitions.
        </p>

        <h3>2.4 Additional Eligibility Requirements (all prize tiers)</h3>
        <p>
          To be eligible for any prize, participants must also: (a) complete the in-app baseline financial literacy and confidence assessment; and (b) meet the minimum activity completion thresholds as defined within the Dough app at the time of program entry. These thresholds will not be increased after the program launch date of April 7, 2026.
        </p>

        <h3>2.5 Employee Eligibility</h3>
        <p>
          Employees, officers, and directors of BetterTime, Co. and U.S. Bank, their respective subsidiaries and affiliated companies, and their immediate family members and household members are not eligible to win prizes. University of St. Thomas students involved in administering or promoting this Program in a paid or official capacity are eligible for Weekly Sim Competition prizes but are not eligible for the Grand Prize Drawing. BetterTime, Co. maintains an internal list of such individuals for audit purposes.
        </p>

        <h3>2.6 Prior Winners</h3>
        <p>
          Confirmed Grand Prize winners of this Program are not eligible to win the Grand Prize in any future BetterYou or BetterTime, Co. partner programs.
        </p>
      </section>

      <section>
        <h2>3. Program Period</h2>
        <p>
          The Program begins at 12:00:00 a.m. Central Time (CT) on April 7, 2026, and ends at 11:59:59 p.m. CT on May 30, 2026 ("Program Period"). The ticket-earning window for the Grand Prize Drawing closes at 11:59:59 p.m. CT on May 3, 2026. Weekly Sim Competitions run throughout the full Program Period.
        </p>
      </section>

      <section>
        <h2>4. How to Participate / Earning Tickets</h2>
        <p>
          To participate, eligible individuals must: (a) download the Dough app (available on the Apple App Store and Google Play Store); (b) create an account; and (c) enter the program access code BREAD-RIGHT-2026 during the Program Period.
        </p>
        <p>
          <strong>Ticket Earning:</strong> Cohort participants earn raffle tickets by completing Educational Lessons, participating in Weekly Sim Competitions, and completing Quiz Mode activities within the Dough app. Additional ticket-earning opportunities may be introduced during the Program Period; the current complete list is always available in the app's "How to Earn Tickets" section. Existing earning opportunities will not be removed during the Program Period. All ticket-earning activities are free to participate in. The number of tickets awarded for each activity is displayed within the app. Tickets earned through eligible activities during the Grand Prize ticket-earning window (April 7 – May 3, 2026) are entered into the Grand Prize Drawing. Tickets cannot be transferred, sold, or assigned.
        </p>
        <p>
          <strong>Eligibility Self-Report:</strong> Upon entering the program access code, participants will be asked to self-report eligibility information. By proceeding, participants attest that the information provided is accurate and grant BetterTime, Co. the right to verify eligibility prior to awarding any prize. Providing false or inaccurate information is grounds for disqualification.
        </p>
      </section>

      <section>
        <h2>5. Weekly Sim Competitions</h2>
        <p>
          Weekly Sim Competitions run each week during the Program Period (Sunday through Saturday). Each week, prizes are awarded as follows:
        </p>

        <div className="my-6 overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="border-b border-border-primary">
                <th className="py-3 pr-4 text-white font-semibold">Place</th>
                <th className="py-3 text-white font-semibold">Prize</th>
              </tr>
            </thead>
            <tbody className="text-text-secondary">
              <tr className="border-b border-border-primary/50">
                <td className="py-3 pr-4">1st place</td>
                <td className="py-3">$100 Tango gift card</td>
              </tr>
              <tr className="border-b border-border-primary/50">
                <td className="py-3 pr-4">2nd–5th place</td>
                <td className="py-3">$50 Tango gift card each</td>
              </tr>
              <tr className="border-b border-border-primary/50">
                <td className="py-3 pr-4">4 random prizes (drawn from top 50% of weekly scorers)</td>
                <td className="py-3">$10 Tango gift card each</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-semibold text-white">Weekly total</td>
                <td className="py-3 font-semibold text-white">$340</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p>
          Winners are determined by final in-app leaderboard rankings following the close of each competition week. Random prizes are selected by Sponsor from among all eligible participants who finish in the top 50% of scorers for that week. Winners are notified in-app on the following business day (typically Monday; Tuesday in weeks following a federal holiday). Tango gift card prizes are delivered by email on the same business day as in-app notification. All Weekly Sim Competition winners are subject to eligibility verification. Sponsor reserves the right, in its sole and absolute discretion, to select an alternate winner if a selected winner is found to be ineligible or fails to respond to prize notification within the specified window.
        </p>
      </section>

      <section>
        <h2>6. Grand Prize Drawing</h2>

        <h3>6.1 Drawing</h3>
        <p>
          On May 4, 2026, Sponsor will conduct a random weighted raffle drawing from all eligible raffle tickets earned by Grand Prize Drawing-eligible participants during the ticket-earning window (April 7 – May 3, 2026). Each ticket represents one entry; participants with more tickets have proportionally greater odds of winning. The drawing will be conducted under the supervision of Sponsor, whose decisions are final and binding. Odds of winning depend on the total number of eligible tickets issued during the ticket-earning window.
        </p>

        <h3>6.2 Potential Winner Notification</h3>
        <p>
          The potential Grand Prize winner will be notified by phone and/or email within five (5) business days of the drawing. The potential winner will have five (5) business days from the time of first notification to respond. If Sponsor is unable to reach the potential winner after two (2) separate contact attempts, or if the potential winner fails to respond within the specified window, the prize will be forfeited and an alternate potential winner will be selected, time and circumstances permitting.
        </p>

        <h3>6.3 Verification</h3>
        <p>
          The potential winner will be required to complete, sign, and return an Affidavit of Eligibility, Liability, and Publicity Release, and IRS Form W-9, within seven (7) calendar days of notification. Failure to return completed documentation within the required timeframe will result in forfeiture of the prize and selection of an alternate potential winner. Additional documentation may be required to confirm eligibility and process payment, as determined by Sponsor in its sole discretion.
        </p>

        <h3>6.4 Winner Announcement</h3>
        <p>
          Public announcement of the Grand Prize winner is subject to completion of eligibility verification and receipt of all required documentation. Sponsor reserves the right to delay announcement pending verification.
        </p>

        <h3>6.5 Prize</h3>
        <p>
          Grand Prize: $2,500 (U.S. dollars), paid as a single one-time payment to the winner's student financial aid account at the University of St. Thomas. If the winner does not have an active student financial aid account, they will be responsible for establishing one prior to payment. U.S. Bank will provide guidance on this process. BetterTime, Co. will remit payment to the University of St. Thomas Financial Aid office within 90 days of Sponsor's receipt of all required documentation from the winner. Final fund availability to the winner is subject to the Financial Aid office's processing timeline. Payment is contingent on confirmation of continued enrollment at the time of remittance. If the winner is no longer enrolled at the University of St. Thomas at the time payment is processed, the prize will be forfeited. The Grand Prize has an approximate retail value of $2,500.
        </p>
      </section>

      <section>
        <h2>7. Taxes</h2>
        <p>
          All federal, state, and local taxes on prizes are the sole responsibility of the winner. Winners are advised to seek independent counsel regarding the tax implications of prize winnings. Grand Prize winners and any Weekly Sim Competition participants whose cumulative prize winnings from this Program equal or exceed $600 in the calendar year will be required to complete IRS Form W-9 as a condition of receiving their prize. BetterTime, Co. will issue IRS Form 1099-MISC to applicable winners as required by law. BetterTime, Co. will contact affected weekly winners with instructions when their cumulative winnings approach or reach the applicable threshold.
        </p>
      </section>

      <section>
        <h2>8. Disqualification</h2>
        <p>
          Sponsor reserves the right, in its sole and absolute discretion, to disqualify any participant at any time during or after the Program Period for: (a) providing false eligibility information; (b) failure to provide proof of eligibility upon request; (c) cheating, fraud, or manipulation of ticket-earning mechanics; (d) using automated, scripted, or other prohibited methods to earn tickets; (e) violation of the Dough app Terms of Service; or (f) any conduct that, in Sponsor's sole judgment, undermines the integrity of the Program. Disqualified participants forfeit all prizes and entries. Sponsor reserves the right to require return of any prize awarded to a disqualified participant.
        </p>
      </section>

      <section>
        <h2>9. Prize Conditions</h2>
        <p>
          Prizes must be accepted as awarded. Prizes cannot be assigned, transferred, or substituted by the winner. Sponsor reserves the right to substitute a prize of equal or greater value if the stated prize becomes unavailable for any reason. In no event is Sponsor responsible for awarding prizes in excess of the number or amounts stated in these rules. If insufficient eligible entries are received for the Grand Prize Drawing, the prize will not be awarded.
        </p>
      </section>

      <section>
        <h2>10. Data Use and Privacy</h2>
        <p>
          By participating, participants consent to BetterTime, Co. collecting, storing, and using personal information provided during registration and program participation for the purposes of administering the Program, verifying eligibility, processing prize awards, and producing aggregate program reports. BetterTime, Co. may share aggregate, non-identifying engagement data with UST and U.S. Bank for program evaluation purposes. For questions about data use, contact <a href="mailto:help@betteryou.ai">help@betteryou.ai</a>.
        </p>
      </section>

      <section>
        <h2>11. Publicity Release</h2>
        <p>
          By accepting a prize, winners (where permitted by law) grant BetterTime, Co. and U.S. Bank the right to use their name, likeness, and general description (e.g., "University of St. Thomas student") for promotional and advertising purposes across any media, without additional compensation. Winners may be required to execute a separate publicity release as part of the Affidavit of Eligibility.
        </p>
      </section>

      <section>
        <h2>12. Limitation of Liability</h2>
        <p className="uppercase-notice">
          BY PARTICIPATING, ENTRANTS AGREE TO RELEASE, DISCHARGE, AND HOLD HARMLESS BETTERTIME, CO., U.S. BANK, THE UNIVERSITY OF ST. THOMAS, AND THEIR RESPECTIVE OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND REPRESENTATIVES FROM ANY AND ALL CLAIMS, LIABILITIES, DAMAGES, OR LOSSES ARISING OUT OF PARTICIPATION IN THE PROGRAM OR THE ACCEPTANCE, USE, OR MISUSE OF ANY PRIZE.
        </p>
        <p>
          Sponsor is not responsible for lost, late, misdirected, or undeliverable notifications; technical failures; or errors of any kind affecting entry, prize delivery, or program administration.
        </p>
      </section>

      <section>
        <h2>13. General Conditions</h2>
        <p>
          Sponsor reserves the right to suspend, modify, or terminate the Program at any time if, in Sponsor's sole judgment, the integrity or fairness of the Program has been compromised. In the event of termination, Sponsor may select winners by random drawing from all eligible entries received prior to termination. Any attempt to deliberately interfere with the legitimate operation of the Program is a violation of these rules and may be subject to legal action.
        </p>
      </section>

      <section>
        <h2>14. Governing Law</h2>
        <p>
          This Program is governed by the laws of the United States and the State of Minnesota, without regard to conflict of law principles. Any disputes arising out of or related to this Program shall be resolved in Hennepin County, Minnesota.
        </p>
      </section>

      <section>
        <h2>15. Void Where Prohibited</h2>
        <p>
          This Program is void where prohibited or restricted by law.
        </p>
      </section>

      <section>
        <h2>Sponsor Contact</h2>
        <p>
          BetterTime, Co.<br />
          370 Wabasha St N, Floor 8<br />
          St. Paul, MN 55102<br />
          <a href="mailto:help@betteryou.ai">help@betteryou.ai</a>
        </p>
        <p>
          Know Your Dough — UST 2026 is presented by U.S. Bank.
        </p>
      </section>
    </LegalPageLayout>
  );
}
